import { ChatModel, type ChatSession } from "@acme/util";
import { atomWithStorage } from "jotai/utils";
import { atom } from "jotai";
import { chatSessionActiveIdAtom, chatSessionsAtom } from "./chat";
import { noteActiveIdAtom, notesAtom } from "./note";
import { teamActiveAtom, userAtom, userSubscriptionAtom } from "./user";
import { savedInstructionsAtom } from "./instruction";
import { savedPromptsAtom } from "./instruction";
import { SpeechLanguageCode } from "../hooks";

type ApiKey = {
    id: string;
    key: string;
    created: Date;
}

export type ChatQuickOption = { enable: boolean, label: string, action: 'regenerate' | 'promptPopup' | 'prompt', value: string, prompt?: string; isAutoSend?: boolean, desc?: string }

export const chatQuickOptionsDefaultAction: ChatQuickOption[] = [
    { enable: true, label: 'Regenerate', action: 'regenerate', value: 'action:::regenerate', desc: 'Regenerate the response' },
    { enable: true, label: 'Prompt', action: 'promptPopup', value: 'action:::prompt', desc: 'Show to Prompt List Popup' },
]

export const chatQuickOptionsDefault: ChatQuickOption[] = [
    ...chatQuickOptionsDefaultAction,
    { enable: false, label: 'Continue', action: 'prompt', value: 'prompt:::Please Continue', prompt: 'Please continue', isAutoSend: true },
    { enable: false, label: 'Make it Short', action: 'prompt', value: 'prompt:::Please Make it Short', prompt: 'Please make it short', isAutoSend: true },
]



export const apiKeyExistAtom = atomWithStorage('apiKeyExist', false)
export const apiKeyAtom = atomWithStorage<ApiKey | null>('apiKey', null)
export const apiKeyAnthropicAtom = atomWithStorage<ApiKey | null>('apiKeyAnthropic', null)
export const apiKeyGoogleAtom = atomWithStorage<ApiKey | null>('apiKeyGoogle', null)
export const apiKeyXaiAtom = atomWithStorage<ApiKey | null>('apiKeyXai', null)
export const apiKeyDeepseekAtom = atomWithStorage<ApiKey | null>('apiKeyDeepseek', null)
export const apiKeyGlmAtom = atomWithStorage<ApiKey | null>('apiKeyGlm', null)
export const apiKeyKimiAtom = atomWithStorage<ApiKey | null>('apiKeyKimi', null)
export const apiKeyQwenAtom = atomWithStorage<ApiKey | null>('apiKeyQwen', null)
export const archivedAtom = atomWithStorage<{ userId: { chats: ChatSession[] } }[]>("chatSessionsArchived", []);

// Tool API Keys (for external services)
export const apiKeyTavilyAtom = atomWithStorage<ApiKey | null>('apiKeyTavily', null)
export const apiKeySerpApiAtom = atomWithStorage<ApiKey | null>('apiKeySerpApi', null)
export const apiKeyFirecrawlAtom = atomWithStorage<ApiKey | null>('apiKeyFirecrawl', null)

// Provider type for API keys
export type ApiKeyProvider = 'openai' | 'anthropic' | 'google' | 'xai' | 'deepseek' | 'glm' | 'kimi' | 'qwen';

// Tool API key providers
export type ToolApiKeyProvider = 'tavily' | 'serpapi' | 'firecrawl';

// Chat Folders
export interface ChatFolder {
    id: string;
    name: string;
    color?: string;
    icon?: string;
    order: number;
    created: Date;
    updated: Date;
}

export const chatFoldersAtom = atomWithStorage<ChatFolder[]>('chatFolders', []);
export const chatFolderMappingAtom = atomWithStorage<Record<string, string>>('chatFolderMapping', {}); // chatId -> folderId

// All API keys consolidated
export interface AllApiKeys {
    openai?: string;
    anthropic?: string;
    google?: string;
    xai?: string;
    deepseek?: string;
    glm?: string;
    kimi?: string;
    qwen?: string;
}

export const speechToTextSettingsAtom = atomWithStorage<{ language: SpeechLanguageCode | null, show: boolean }>('speechToTextSettings', { language: null, show: false })
export const textToSpeechSettingsAtom = atomWithStorage<{ language: SpeechLanguageCode, voiceURI: string, rate: number, pitch: number, showButton: boolean, show: boolean }>('textToSpeechSettings', {
    language: 'en-US', voiceURI: 'Daniel', rate: 1, pitch: 1, showButton: true, show: false
})


export const generalDefaultSettingsAtom = atomWithStorage<{ soundCompletion?: boolean }>('generalDefaultSettings', { soundCompletion: true })
export const chatDefaultSettingsAtom = atomWithStorage<{ defaultModel?: ChatModel, defaultMaxContextTokens?: number | 'auto' | '' }>(
    'chatDefaultSettings',
    {}
)
export const chatQuickOptionsAtom = atomWithStorage<{ list: ChatQuickOption[] }>('chatQuickOptions', { list: chatQuickOptionsDefault })

export const deleteNonSyncAtom = atom(null,
    (get, set, param?: { removeNonSync?: boolean }) => {
        const chats = get(chatSessionsAtom);
        const notes = get(notesAtom);

        set(chatSessionActiveIdAtom, null);
        set(noteActiveIdAtom, null);

        const chatsToKeep = chats.filter(chat => !chat.isSync);
        const notesToKeep = notes.filter(note => !note.isSync);

        set(userAtom, { id: "" })
        set(userSubscriptionAtom, null)
        set(teamActiveAtom, null)
        set(savedInstructionsAtom, [])
        set(savedPromptsAtom, [])

        if (param?.removeNonSync) {
            set(chatSessionsAtom, []);
            set(notesAtom, []);
        } else {
            set(chatSessionsAtom, chatsToKeep);
            set(notesAtom, notesToKeep);
        }
        set(apiKeyAtom, null)
        set(apiKeyAnthropicAtom, null)
        set(apiKeyGoogleAtom, null)
        set(apiKeyXaiAtom, null)
        set(apiKeyDeepseekAtom, null)
        set(apiKeyGlmAtom, null)
        set(apiKeyKimiAtom, null)
        set(apiKeyQwenAtom, null)
        set(apiKeyExistAtom, false)
    }
)
